import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useQueryClient } from '@tanstack/react-query'
import { useI18n } from 'builder/hooks/useI18n'
import {
  actions as jobSearchActions,
  selectors as jobSearchSelectors,
} from 'builder/modules/jobSearch'
import {
  AlertTypes,
  CustomAlertType,
  JobSearchNotificationType,
  RecommendAlert,
} from 'builder/modules/jobSearch/types'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { ConfigScopesEnum } from 'builder/modules/init'
import { useConfig } from 'builder/hooks/useConfig'
import Spinner from 'builder/components/Spinner'
import { ScrollabelDialogModalV2 } from 'builder/components/FindJob/AlertModal/ManageYourJobAlerts/ScrollabelDialogModalV2/ScrollabelDialogModalV2'
import { useCareerProfileData } from 'builder/hooks/useCareerProfileData'
import { useGetCustomAlerts } from '../hooks/useGetCustomAlerts'
import { TabType } from '../../types'
import { useGetRecommendationAlert } from '../hooks/useGetRecommendationAlert'
import { useMutationRecommendationAlert } from '../hooks/useMutationRecommendationAlert'
import { useMutationCustomAlert } from '../hooks/useMutationCustomAlert'
import { generateEditCustomAlert } from '../utils'
import { CreateCustomAlertButton } from './CreateCustomAlertButton/CreateCustomAlertButton'
import { CustomAlert } from './CustomAlert/CustomAlert'
import { Content, SpinnerContainer } from './styles'
import { BlockedRecommendedAlert } from './Card/BlockedRecommendedAlert/BlockedRecommendedAlert'
import { EmailNotification } from './EmailNotification/EmailNotification'
import { RecommendedAlert } from './Card/RecommendedAlert/RecommendedAlert'

const TRANSLATION = 'builder.job_search.alert_modal.manage_your_job_alerts'
interface Props {
  selectedTab?: TabType
}
export const ManageYourJobAlerts = ({ selectedTab }: Props) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const enableJobAlertFeature = useTypedSelector(jobSearchSelectors.enableJobAlertFeature)
  const countryConfig = useConfig(ConfigScopesEnum.country)
  const customAlerts = useGetCustomAlerts(enableJobAlertFeature)
  const recommendationAlert = useGetRecommendationAlert()
  const mutationRecommendationAlert = useMutationRecommendationAlert()
  const mutationCustomAlert = useMutationCustomAlert()

  const { careerProfileData: careerProfile } = useCareerProfileData()
  const searchForm = useTypedSelector(jobSearchSelectors.searchForm)
  const queryClient = useQueryClient()

  const missingProfileTargetRole =
    !careerProfile?.targetRoles ||
    careerProfile?.targetRoles.length === 0 ||
    careerProfile?.targetRoles[0].standardTitle === ''

  const missingResumeTargetRole =
    !careerProfile?.resumeTargetRoles || careerProfile?.resumeTargetRoles.length === 0

  const blockedRecommendedAlert = missingProfileTargetRole && missingResumeTargetRole

  const turnOnValue = !!(
    recommendationAlert.data?.active || customAlerts.data?.find(alert => alert.active)
  )

  const toggleEmailNotification = async () => {
    let recommendPromise: Promise<RecommendAlert> | undefined

    const customAlertPromises = []
    if (!blockedRecommendedAlert) {
      recommendPromise = mutationRecommendationAlert.mutateAsync({
        data: { active: !turnOnValue },
        shouldUpdateCache: false,
      })
    }

    for (const customAlert of customAlerts.data || []) {
      if (customAlert.active === turnOnValue) {
        customAlertPromises.push(
          mutationCustomAlert.mutateAsync({
            data: { id: customAlert.id, active: !turnOnValue },
            shouldUpdateCache: false,
          }),
        )
      } else {
        customAlertPromises.push(Promise.resolve(customAlert))
      }
    }

    try {
      trackInternalEvent('turn_alerts_button', { parameter: !turnOnValue ? 'on' : 'off' })
      const recommendAlert = await recommendPromise
      const updatedCustomAlerts = await Promise.all(customAlertPromises)
      if (recommendAlert) {
        queryClient.setQueryData<RecommendAlert>(
          ['useGetRecommendationAlert'],
          () => recommendAlert,
        )
      }

      queryClient.setQueryData<CustomAlertType[]>(['useGetCustomAlerts'], () => updatedCustomAlerts)
    } catch (e) {
      dispatch(jobSearchActions.setNotification(JobSearchNotificationType.snackbar_fail))
    }
  }

  const renderBody = () => {
    if (customAlerts.isLoading || recommendationAlert.isLoading) {
      return (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      )
    }

    return (
      <>
        <Content>
          <EmailNotification enabled={turnOnValue} onClick={toggleEmailNotification} />
          {blockedRecommendedAlert ? (
            <BlockedRecommendedAlert />
          ) : (
            <RecommendedAlert
              recommendedAlert={recommendationAlert.data}
              onClick={() => {
                dispatch(
                  jobSearchActions.setAlertType({
                    type: AlertTypes.edit_recommend_alert,
                  }),
                )
                trackInternalEvent('customize_job_alerts', { parameter: 'recommend_alert' })
              }}
            />
          )}
          {(customAlerts.data || []).map(alert => (
            <CustomAlert
              key={alert.id}
              {...alert}
              onClick={() => {
                dispatch(
                  jobSearchActions.setAlertType({
                    type: AlertTypes.edit,
                    editCustomAlert: alert,
                  }),
                )
                trackInternalEvent('customize_job_alerts', { parameter: 'custom_alert' })
              }}
            />
          ))}
        </Content>
        {countryConfig &&
          customAlerts.data &&
          countryConfig.jobAlertsLimit > customAlerts.data.length && (
            <CreateCustomAlertButton
              onClick={() => {
                let editCustomAlert: CustomAlertType | undefined

                if (selectedTab && selectedTab === TabType.search) {
                  editCustomAlert = generateEditCustomAlert({
                    targetRole: searchForm.term,
                    filters: searchForm.filters,
                    selectedLocation: searchForm.selectedLocation,
                  })
                }
                dispatch(
                  jobSearchActions.setAlertType({
                    type: AlertTypes.create,
                    editCustomAlert,
                  }),
                )
              }}
            />
          )}
      </>
    )
  }

  return (
    <ScrollabelDialogModalV2
      onClose={() => dispatch(jobSearchActions.setAlertType({ type: undefined }))}
      title={i18n.t(`${TRANSLATION}.title`)}
    >
      {renderBody()}
    </ScrollabelDialogModalV2>
  )
}
